// LearnCore Frontend - 用户仪表盘页面

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import type { Tag } from '../types';
import Loading from '../components/Loading';
import type { StudySet } from '../types';
import {
  PlusIcon,
  BookOpenIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ShareIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

export default function DashboardPage() {
  const { user } = useAuth();
  const [studySets, setStudySets] = useState<StudySet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [_nickname, setNickname] = useState<string | undefined>(undefined);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [activeTagIds, setActiveTagIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSets, setFilteredSets] = useState<StudySet[]>([]);

  useEffect(() => {
    loadStudySets();
    loadAllTags();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const resp = await apiClient.getUserSettings();
        setNickname(resp.settings.profile?.nickname);
      } catch {}
    };
    if (user) fetchSettings();
  }, [user]);

  const loadStudySets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getStudySets({ tags: Array.from(activeTagIds) });
      // 解析标签数据
      const setsWithParsedTags = response.sets.map(set => ({
        ...set,
        tags: set.tags ? (typeof set.tags === 'string' ? JSON.parse(set.tags) : set.tags) : []
      }));
      setStudySets(setsWithParsedTags);
    } catch (err: any) {
      setError(err.response?.data?.error || '获取学习集失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 根据搜索查询过滤学习集
    if (searchQuery.trim() === '') {
      setFilteredSets(studySets);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = studySets.filter(set =>
        set.title.toLowerCase().includes(query) ||
        (set.description && set.description.toLowerCase().includes(query))
      );
      setFilteredSets(filtered);
    }
  }, [searchQuery, studySets]);

  const loadAllTags = async () => {
    try {
      const [grades, subjects] = await Promise.all([
        apiClient.getTags('grade'),
        apiClient.getTags('subject')
      ]);
      setAllTags([...(grades.tags||[]), ...(subjects.tags||[])]);
    } catch {}
  };

  const toggleActiveTag = (id: string) => {
    setActiveTagIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    // 改变标签筛选时，重新加载
    loadStudySets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTagIds]);

  const handleDelete = async (setId: string) => {
    if (!confirm('确定要删除这个学习集吗？此操作不可撤销。')) {
      return;
    }

    try {
      setDeletingId(setId);
      await apiClient.deleteStudySet(setId);
      setStudySets(prev => prev.filter(set => set.id !== setId));
    } catch (err: any) {
      alert(err.response?.data?.error || '删除失败');
    } finally {
      setDeletingId(null);
    }
  };

  const handleShare = async (setId: string) => {
    const url = `${window.location.origin}/sets/${setId}/view`;
    try {
      await navigator.clipboard.writeText(url);
      alert('链接已复制到剪贴板！');
    } catch (err) {
      prompt('复制下面的链接:', url);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('zh-CN');
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading size="lg" text="加载学习集..." />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
              <DocumentTextIcon className="h-8 w-8 mr-3 text-primary-600" />
              我的学习集
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              创建、管理和学习你的卡片学习集
            </p>
          </div>

          <div className="flex gap-3">
            <Link
              to="/sets/create"
              className="btn-primary flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              创建学习集
            </Link>
          </div>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className="mb-6 bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="搜索标题或描述..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 标签筛选 */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {allTags.map(tag => (
            <button
              key={tag.id}
              type="button"
              onClick={() => toggleActiveTag(tag.id)}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                activeTagIds.has(tag.id)
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 border-primary-300 dark:border-primary-700'
                  : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
              }`}
              title={tag.type}
            >
              {tag.name}
            </button>
          ))}
          {allTags.length === 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500">暂无标签</span>
          )}
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="alert-danger mb-6">
          {error}
          <button 
            onClick={loadStudySets}
            className="ml-4 text-sm underline hover:no-underline"
          >
            重试
          </button>
        </div>
      )}

      {/* 统计信息 */}
      <div className="mb-6 text-center">
        <div className="inline-flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-4 py-2 rounded-lg">
          <div className="flex items-center">
            <BookOpenIcon className="h-4 w-4 mr-1" />
            <span>共 {studySets.length} 个学习集</span>
          </div>
          {searchQuery && (
            <div className="flex items-center">
              <MagnifyingGlassIcon className="h-4 w-4 mr-1" />
              <span>找到 {filteredSets.length} 个匹配结果</span>
            </div>
          )}
        </div>
      </div>

      {/* 学习集列表 */}
      {filteredSets.length === 0 ? (
        <div className="text-center py-12">
          <DocumentTextIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {searchQuery ? '没有找到匹配的学习集' : '还没有学习集'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchQuery
              ? '尝试调整搜索条件或查看所有学习集。'
              : '创建你的第一个学习集，开始学习之旅。'
            }
          </p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="btn-outline mr-4"
            >
              清除搜索
            </button>
          )}
          <Link
            to="/sets/create"
            className="btn-primary"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            创建学习集
          </Link>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredSets.map((studySet) => (
            <div 
              key={studySet.id} 
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col"
            >
              <div className="p-5 flex-1 flex flex-col">
                {/* 创建日期 + 公开/私人状态 - 置顶 */}
                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-3">
                  <span className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {formatDate(studySet.updated_at)}
                  </span>
                  
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    studySet.is_public
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                  }`}>
                    {studySet.is_public ? '公开' : '私人'}
                  </span>
                </div>

                {/* 标题 */}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
                  {studySet.title}
                </h3>

                {/* 描述 - 固定高度区域 */}
                <div className="mb-3 min-h-[2.5rem]">
                  {studySet.description ? (
                    <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">
                      {studySet.description}
                    </p>
                  ) : (
                    <div className="text-gray-400 text-sm italic">暂无描述</div>
                  )}
                </div>

                {/* 标签展示 - 固定高度区域 */}
                <div className="mb-4 min-h-[1.5rem]">
                  {Array.isArray(studySet.tags) && studySet.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {studySet.tags.map((t: any) => (
                        <span key={t.id} className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                          {t.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-400 text-xs italic">暂无标签</div>
                  )}
                </div>

                {/* 操作按钮 - 置底 */}
                <div className="flex space-x-2 mt-auto">
                  <Link
                    to={`/sets/${studySet.id}/study`}
                    className="flex-1 btn-primary text-center text-sm py-2"
                  >
                    学习
                  </Link>
                  
                  <Link
                    to={`/sets/${studySet.id}/view`}
                    className="btn-outline px-3 py-2 text-sm"
                    title="查看"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Link>
                  
                  <Link
                    to={`/sets/${studySet.id}/edit`}
                    className="btn-outline px-3 py-2 text-sm"
                    title="编辑"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Link>
                  
                  <button
                    onClick={() => handleShare(studySet.id)}
                    className="btn-outline px-3 py-2 text-sm"
                    title="分享"
                  >
                    <ShareIcon className="h-4 w-4" />
                  </button>

                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 px-2">
                    <div className="flex items-center">
                      <BookOpenIcon className="h-3 w-3 mr-1" />
                      <span>{(studySet as any).flashcard_count ?? 0} 张卡片</span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleDelete(studySet.id)}
                    disabled={deletingId === studySet.id}
                    className="btn-outline px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:border-red-300 disabled:opacity-50"
                    title="删除"
                  >
                    {deletingId === studySet.id ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                    ) : (
                      <TrashIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
